import { useQuery } from '@apollo/client';
import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';

import { ME_QUERY } from '../graphql/queries';
import { WELCOME_FLOW_FLAG, welcomeCompletedKey } from '../constants/onboarding';
import { useFeatureFlag } from './useFeatureFlag';

/**
 * Synchronous backstop so a freshly-completed user is never treated as needing
 * onboarding while the ME_QUERY refetch is still in flight.
 */
function hasJustCompleted(asfId: string | undefined): boolean {
  if (!asfId) return false;
  try {
    return !!sessionStorage.getItem(welcomeCompletedKey(asfId));
  } catch {
    return false;
  }
}

/** Whether the signed-in user still owes the welcome wizard. `ready` is false until we can tell. */
export function useNeedsOnboarding() {
  const { isSignedIn } = useClerkAuth();
  const { user: clerkUser } = useUser();

  const { data: meData } = useQuery(ME_QUERY, {
    skip: !isSignedIn,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const { enabled: welcomeEnabled, loading: flagLoading } = useFeatureFlag(WELCOME_FLOW_FLAG);

  const user = meData?.user;
  const ready = Boolean(user) && !flagLoading;

  if (!ready) return { needsOnboarding: false, ready };
  if (!welcomeEnabled) return { needsOnboarding: false, ready };

  const asfId = clerkUser?.publicMetadata?.asfId as string | undefined;
  const needsOnboarding = !user.welcomeCompletedAt && !hasJustCompleted(asfId) && !user.hasCommunityActivity;

  return { needsOnboarding, ready };
}
